import { useEffect, useRef, useState } from "react";

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const pressureCache = new Map();
const LS_KEY = "the99.cache.barometric";

function readPersistedPressure() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistPressure(value) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(value)); } catch {}
}

function getCacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

function hpaToInHg(hpa) {
  return hpa * 0.029529983071445;
}

function trendFromDelta(deltaHpa) {
  if (deltaHpa > 0.5) return "Rising";
  if (deltaHpa < -0.5) return "Falling";
  return "Steady";
}

function formatUpdatedAt(date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export default function useBarometricPressure(location, { isOffline = false } = {}) {
  const [data, setData] = useState({
    label: "Loading…",
    status: "warn",
    pressureInHg: null,
    trend: null,
    updatedAt: null,
    updatedLabel: null,
    error: null,
    loading: false,
  });
  const [refreshToggle, setRefreshToggle] = useState(0);
  const forceRefreshRef = useRef(false);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };

  useEffect(() => {
    if (!location?.lat || !location?.lng) {
      setData({
        label: "GPS needed",
        status: "warn",
        pressureInHg: null,
        trend: null,
        updatedAt: null,
        updatedLabel: null,
        error: "GPS needed",
        loading: false,
      });
      return;
    }

    const fetchPressure = async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const cacheKey = getCacheKey(location.lat, location.lng);
      const cached = pressureCache.get(cacheKey);

      setData((prev) => ({ ...prev, loading: true }));

      // If offline, return persisted value immediately
      if (isOffline || !navigator.onLine) {
        const persisted = readPersistedPressure();
        if (persisted) {
          setData({ ...persisted, loading: false });
        } else {
          setData({ label: "Offline", status: "warn", pressureInHg: null, trend: null, updatedAt: null, updatedLabel: null, error: "Offline", loading: false });
        }
        return;
      }

      if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setData(cached.value);
        return;
      }

      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lng}&current=pressure_msl&hourly=pressure_msl&past_hours=3&forecast_hours=1`
        );

        if (!response.ok) {
          throw new Error("Barometric API request failed");
        }

        const json = await response.json();
        const currentHpa = json?.current?.pressure_msl;
        const hourlyValues = json?.hourly?.pressure_msl;

        if (typeof currentHpa !== "number") {
          throw new Error("Pressure data unavailable");
        }

        let previousHpa = null;
        if (Array.isArray(hourlyValues) && hourlyValues.length >= 2) {
          previousHpa = hourlyValues[hourlyValues.length - 2];
        }

        const deltaHpa = typeof previousHpa === "number" ? currentHpa - previousHpa : 0;
        const trend = trendFromDelta(deltaHpa);
        const pressureInHg = hpaToInHg(currentHpa);
        const updatedAt = new Date();

        const value = {
          label: `${pressureInHg.toFixed(2)} → ${trend}`,
          status: "good",
          pressureInHg,
          trend,
          updatedAt,
          updatedLabel: formatUpdatedAt(updatedAt),
          error: null,
          loading: false,
        };

        pressureCache.set(cacheKey, {
          value,
          timestamp: Date.now(),
        });
        persistPressure(value);
        setData(value);
      } catch (error) {
        console.error("Barometric fetch failed:", error);
        setData({
          label: "Unavailable",
          status: "warn",
          pressureInHg: null,
          trend: null,
          updatedAt: null,
          updatedLabel: null,
          error: "Unavailable",
          loading: false,
        });
      }
    };

    fetchPressure();
  }, [location?.lat, location?.lng, refreshToggle, isOffline]);

  return { ...data, refresh };
}
